// What the two listing rows (HUBX-8480 inline, HUBX-8481 expanded) show AROUND their form,
// read off the v2 row and its writableFields - never off the role and the active tab, which
// is where the legacy row took most of it from. Pure, like the write rules: the rows call
// these, and the answers are tested without TestBed.

import { ItemResponse } from '../item-write/item.models';
import {
  APPROVAL_NOTICE, APPROVAL_NOTICE_STAYS_PAUSED, REACTIVATION_NOTICE, REVIEW_NOTICE, archiveIntentNotice, manualReactivationNotice,
  savedStaysPausedNote, withStaysPaused
} from '../item-write/rules/item-write-messages';
import {
  IReviewedValues, isApprovalPending, isManualReactivationPending, isReactivationPending, isReviewPending, staysPausedAfterSave
} from '../item-write/rules/item-write-status.rules';
import { isReadOnly } from '../item-write/rules/item-write-writability.rules';
import { IListingRow, ListingTab } from './item-listing.models';

export type ListingRole = 'SELLER' | 'ADMIN' | 'VM';

/**
 * The company named in the Part Number tooltip (shown under a global search, which spans
 * vendors). Legacy parity, from the v2 row:
 * - a Seller sees one company, so nothing is named;
 * - an operator sees the row's company (`companyName`, operators only);
 * - a VendorManager sees it too on a vendor it may write for, and otherwise who owns the
 *   vendor - "Laura's vendor", or "Unassigned vendor". Legacy decided "may write" from its own
 *   copy of the VM portfolio; v2 says it per row: an empty writableFields is outside the
 *   caller's write scope (HUBX-8492). The owner map is still the legacy
 *   getVendorManagerDiccionary, keyed by company.
 */
export function rowSellerLabel(role: ListingRole, row: IListingRow, vmOwners?: Map<string, string>): string {
  if (role === 'SELLER') { return ''; }
  if (role === 'VM' && isReadOnly(row.item.writableFields ?? [])) {
    const owner = row.companyId ? vmOwners?.get(row.companyId) : undefined;
    return owner ? `${owner}'s vendor` : 'Unassigned vendor';
  }
  return row.companyName ?? '';
}

/**
 * Whether the whole row is inert: a read-only portal (a SellerAutomation vendor's own users,
 * 2026-09-11), or a row the server gave nothing to write (HUBX-8493, the rule the Edit modal
 * already reads). Anything short of that is writableFields' call control by control
 * (applyWritability) - which is what replaces legacy's "Laptop Plaza" / "HUBX" seller-name
 * check on Cost and its private VendorManager portfolio lookup.
 */
export function isRowLocked(portalReadOnly: boolean, writableFields: string[]): boolean {
  return portalReadOnly || isReadOnly(writableFields ?? []);
}

/** The standing line on a Removed row that a cost cut would bring back. The percent is the
 *  band's own, as the server sends it beside the threshold (HUBX-8494): legacy computed it
 *  here from the reductionRules bands, which v2 never sends to the client. */
export function reactivationHint(row: IListingRow): string | null {
  if (row.item.businessStatus !== 'Removed' || row.reactivationThreshold == null || row.reactivationReductionPercent == null) {
    return null;
  }
  return `In order to reactivate, reduce cost by ${Number(row.reactivationReductionPercent)}%`;
}

/** Whether the row takes part in the Removed tab's bulk "reduce cost" (checkbox + one
 *  button setting each checked row's cost to its threshold). Legacy offered it on every
 *  Removed row with a threshold; a row whose cost this caller may not write cannot take it. */
export function isSelectableForReactivation(tab: ListingTab, row: IListingRow): boolean {
  return tab === 'Removed'
    && row.reactivationThreshold != null
    && (row.item.writableFields ?? []).includes('unitPurchasePrice');
}

/**
 * The popup's four notices for what SAVE will do beyond what was typed - approval,
 * re-review, reactivation by the cost cut, manual reactivation (HUBX-8476) - from the same
 * rules and in the same words (item-write-messages), so the row and the popup cannot
 * disagree. At most one of re-review and reactivation can hold: one needs a cost raise or an
 * approved item, the other a Removed item and a cut below the threshold. The two
 * reactivations exclude each other by construction.
 *
 * Who may approve comes from the ROLE: writableFields carries `status` for every role
 * (ADR-0009 Rule 9), so it cannot tell an approver from a Seller. Notices only — the server
 * decides what Save does.
 *
 * `isArchived` is the row's Archive/Restore intent: when it differs from the stored flag the
 * row says it will be archived or restored on Save, as the Edit modal does (HUBX-8466). Last,
 * as in the modal. Omitted, no archive notice.
 *
 * `reviewed` is the row's form (getRawValue()): the inline row edits ETA, MOQ and MXQ, the
 * expanded one Special Notes and Restrictions too, and a Seller's change to any of them on an
 * approved item sends it back for review as a cost raise does (HUBX-8515). Omitted, the
 * re-review reads the cost alone.
 */
export function rowNotices(
  skuStatus: string,
  cost: unknown,
  baseline: ItemResponse,
  reactivationThreshold: number | null,
  role: ListingRole,
  isArchived?: boolean,
  reviewed?: IReviewedValues
): string[] {
  const writable = baseline.writableFields ?? [];
  const approver = role !== 'SELLER';
  // The vendor paused it: approved or reactivated, it stays Paused (HUBX-8466).
  const paused = staysPausedAfterSave(skuStatus, baseline, approver);
  const notices: string[] = [];
  if (isApprovalPending(skuStatus, baseline, approver)) { notices.push(paused ? APPROVAL_NOTICE_STAYS_PAUSED : APPROVAL_NOTICE); }
  if (isReviewPending(cost, baseline, approver, reviewed)) { notices.push(REVIEW_NOTICE); }
  if (isReactivationPending(cost, baseline, writable, reactivationThreshold)) { notices.push(withStaysPaused(REACTIVATION_NOTICE, paused)); }
  if (isManualReactivationPending(skuStatus, cost, baseline, writable, reactivationThreshold, approver)) {
    notices.push(withStaysPaused(manualReactivationNotice(reactivationThreshold), paused));
  }
  const archive = isArchived === undefined ? null : archiveIntentNotice(isArchived, baseline);
  if (archive) { notices.push(archive); }
  return notices;
}

/** The line that replaces "Saved." on a row whose save approved or reactivated an item the
 *  vendor keeps paused (HUBX-8466), or null for the plain one. Read off the status the row
 *  sent and the row as stored before Save: the save itself returns no state. */
export function rowSavedNote(status: string, baseline: ItemResponse | undefined, role: ListingRole): string | null {
  return baseline && staysPausedAfterSave(status, baseline, role !== 'SELLER')
    ? savedStaysPausedNote(baseline.businessStatus)
    : null;
}
